import React from 'react';
import { ArrowForwardIos } from '@mui/icons-material';
import { Avatar, Box, ListItemAvatar, ListItemButton, ListItemText, Typography } from '@mui/material';
import { PhysicianSupervisee } from '@uplift/api';
import { DateFormats, providerSuperviseeRoute } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { useCollaborativeStateDetailsQuery, useStatesQuery } from '@uplift/queries';
import { useHistory } from 'react-router';
import { CalendarDateService } from '@uplift/services';
import { getInitials, getPrescriberLabel } from '@uplift/utils';


export type SuperviseeListItemProps = {
  supervisee: PhysicianSupervisee;
};


export const SuperviseeListItem: React.VFC<SuperviseeListItemProps> = ({ supervisee }) => {
  const l10n = useL10n();
  const history = useHistory();
  const { data: states = [] } = useStatesQuery();
  const { data: collaborativeStateDetails } = useCollaborativeStateDetailsQuery(supervisee.stateId);
  const prescriberLabel = getPrescriberLabel(supervisee.prescriberType, l10n.resources.provider.prescriberTypes);
  const initials = supervisee.user.firstName && supervisee.user.lastName ? getInitials(supervisee.user.firstName, supervisee.user.lastName) : '?';
  const licensedLabel = states
    .filter(state => supervisee.licenseStateIds.includes(state.id))
    .map(state => state.shortName)
    .join(', ');
  const formattedDueDate = CalendarDateService.format(supervisee.nextDueDate, DateFormats['MMM D, YYYY']);
  const isPastDue = supervisee.nextDueDate < new Date().toISOString().slice(0, 10);
  const dueText = isPastDue ? `Past due - was ${formattedDueDate}` : `Next meeting due ${formattedDueDate}`;

  return (
    <ListItemButton onClick={() => history.push(providerSuperviseeRoute.createLink({ superviseeId: supervisee.id }))}>
      <ListItemAvatar>
        <Avatar sx={{ fontSize: '0.875rem' }}>{initials}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography component="span" sx={{ fontWeight: 500 }}>
              {supervisee.user.firstName} {supervisee.user.lastName}
            </Typography>
            {prescriberLabel && (
              <Typography component="span" variant="body2" color="text.secondary">
                {prescriberLabel}
              </Typography>
            )}
          </Box>
        )}
        secondary={[
          `Current Caseload: ${supervisee.currentCaseload}`,
          licensedLabel && `Licensed: ${licensedLabel}`,
        ].filter(Boolean).join(' · ')}
      />
      {collaborativeStateDetails?.requiresSupervisionMeeting && (
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1, whiteSpace: 'nowrap', fontWeight: isPastDue ? 'bold' : 'normal' }}>
          {dueText}
        </Typography>
      )}
      <ArrowForwardIos sx={{ fontSize: '14px', color: 'text.secondary' }} />
    </ListItemButton>
  );
};
